import { memo } from "react";
import { Handle, Position, type NodeProps, type Node } from "@xyflow/react";
import { CreditCard, Layers, Play, Square, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { EditorStep, EditorCardGroupStep } from "@/types/activity";

export type StepNodeData = {
  step: EditorStep;
  isFirstStep: boolean;
  isEndStep: boolean;
  hasErrors?: boolean;
};

type StepNode = Node<StepNodeData, "stepNode">;

function getCardContentTitle(content: Record<string, unknown>): string {
  // Prioritise common title keys
  for (const key of ["title", "question", "label", "name"]) {
    const val = content[key];
    if (typeof val === "string" && val.trim()) {
      const t = val.trim();
      return t.length > 30 ? t.slice(0, 17) + "…" : t;
    }
  }
  // Fallback: first non-empty string value
  for (const val of Object.values(content)) {
    if (typeof val === "string" && val.trim()) {
      const t = val.trim();
      return t.length > 30 ? t.slice(0, 17) + "…" : t;
    }
  }
  return "";
}

function StepNodeComponent({ data, selected }: NodeProps<StepNode>) {
  const { step, isFirstStep, isEndStep, hasErrors } = data;
  const isCard = step.type === "card";
  const group = !isCard ? (step as EditorCardGroupStep) : null;
  const label = isCard
    ? step.templateLabel || "Carte"
    : group!.groupType === "quiz"
      ? "Quiz"
      : group!.groupType === "action"
        ? "Action"
        : group!.groupType;
  const subtitle = isCard
    ? getCardContentTitle(step.content)
    : [group!.title, `${group!.cards.length} carte(s)`].filter(Boolean).join(" · ");

  return (
    <div
      className={`
        relative rounded-lg border-2 bg-card px-4 py-3 shadow-sm min-w-[200px] max-w-[240px]
        transition-all
        ${hasErrors
          ? "border-destructive ring-2 ring-destructive/30"
          : selected
            ? isCard ? "border-primary ring-2 ring-primary/30" : "border-group ring-2 ring-group/30"
            : isCard ? "border-border" : "border-group-pale"}
        ${isFirstStep && !hasErrors ? "border-success" : ""}
      `}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-primary !border-2 !border-primary-foreground"
      />

      <div className="flex items-center gap-2 mb-1">
        {hasErrors ? (
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
        ) : isCard ? (
          <CreditCard className="h-4 w-4 text-primary shrink-0" />
        ) : (
          <Layers className="h-4 w-4 text-group shrink-0" />
        )}
        <span className="text-sm font-medium truncate">{label}</span>
        {isFirstStep && (
          <Badge variant="outline" className="text-[10px] px-1 py-0 border-success text-success shrink-0">
            <Play className="h-2.5 w-2.5 mr-0.5" />
            Départ
          </Badge>
        )}
        {isEndStep && (
          <Badge variant="outline" className="text-[10px] px-1 py-0 border-destructive text-destructive shrink-0">
            <Square className="h-2.5 w-2.5 mr-0.5" />
            Fin
          </Badge>
        )}
      </div>

      {!isCard && ((step as EditorCardGroupStep).config as any)?.meteo_score_type && (
        <Badge variant="outline" className="text-[10px] px-1 py-0 mt-0.5 border-primary/40 text-primary">
          {((step as EditorCardGroupStep).config as any)?.meteo_score_type === "wellbeing"
            ? "Bien-être"
            : "Souffrance"}
        </Badge>
      )}

      {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-primary !border-2 !border-primary-foreground"
      />
    </div>
  );
}

export default memo(StepNodeComponent);
